import type { FAQItem } from '../types/faq'

export const faq: FAQItem[] = [
    {
        id: 0,
        question: 'What does the 50% deposit cover, and when is the rest due?',
        answer:
            'The 50% non-refundable deposit secures your session date and time. The remaining 50% is due at the end of the session day, along with GST and any additional fees (for example: extra editing time, Pro Tools surcharge, or overtime). A detailed invoice is provided at the end of the day.',
    },
    {
        id: 1,
        question: 'Can I reschedule or cancel after paying the deposit?',
        answer:
            'Yes. A reschedule link is included in your initial booking email, and you can move your session to another available slot within our 90-day booking window at no extra cost. Deposits are non-refundable in most cases, but we make exceptions for serious health or injury situations. Rehearsal bookings are refundable if cancelled at least 3 days before the booking date. For dates beyond 90 days, contact us via the form or email.',
    },
    {
        id: 2,
        question: 'How long is a full-day session? Do you offer half-day or hourly bookings?',
        answer:
            'A full-day session is 8 hours, typically 10am-6pm. We also offer half-day and hourly bookings (2-hour minimum), which are arranged directly via email or the contact form rather than through the online calendar.',
    },
    {
        id: 3,
        question: 'Are recording and rehearsal rates the same?',
        answer:
            'No. Recording includes an engineer for the full duration of the session. Rehearsal includes an engineer for roughly the first 20 minutes to help with setup and soundcheck, then you have the room. Current rates and options are outlined in our pricing table, which we can share during your consult or on request.',
    },
    {
        id: 4,
        question: 'What’s included with the rehearsal space, and what should we bring?',
        answer:
            'Rehearsal bookings include three floor wedges with 15-inch speakers plus a subwoofer, three vocal mics, a selection of guitar and bass amps, and a Taye drum kit. Many drummers prefer to bring their own snare and cymbals, but we can provide those if needed. Please bring your own expendables like sticks, picks, and strings.',
    },
    {
        id: 5,
        question: 'Can we leave our gear set up between rehearsals (lockout)?',
        answer:
            'Yes. Lockout can be accommodated for recurring or multi-day use of the room, but it incurs an additional charge. Let us know what you have in mind and we will quote based on schedule and storage needs.',
    },
    {
        id: 6,
        question: 'Are rehearsals recorded?',
        answer:
            "We don’t multitrack record rehearsals by default—that’s part of our recording service. However, you're welcome to record a 2-track mix off the board for reference. Our engineer can help you set this up at the start of your rehearsal.",
    },
    {
        id: 7,
        question: 'Which DAWs do you support, and can I bring my own sessions?',
        answer:
            'We currently run Logic Pro X 11.2.2, Ableton Live 11, and Reaper 7.38. Pro Tools can be arranged on a per-project basis for a small additional fee. You are welcome to bring your own sessions; we will print any important mix or buss effects before you take the project back to your home studio to avoid plugin conflicts.',
    },
    {
        id: 8,
        question: 'What sample rate and bit depth do you use, and what can I receive after the session?',
        answer:
            'We typically record at 48 kHz / 24-bit. We can deliver stereo mixes, instrumentals or a cappellas, stems, cut-downs, and other formats requested by your distributor, publisher, or mix/mastering engineer, as long as we can complete the exports within your allotted session time. Overtime is billed at $110 per hour.',
    },
    {
        id: 9,
        question: 'Do you offer editing, tuning, and drum quantization?',
        answer:
            'Yes. We provide full editing services, including vocal tuning, drum quantisation, and tightening/comping takes. Editing is billed at $110 per hour (2-hour minimum) and can be booked as part of a session or as a follow-up block.',
    },
    {
        id: 10,
        question: 'Can I attend mixing or mastering sessions remotely?',
        answer:
            'Yes. You can sit in on mixing and mastering sessions via Sessionwire. We use assignable two-way high-quality audio streams between studios and can host up to 50 guest seats if you want bandmates, producers, or label contacts to join. Our mixing rate includes two rounds of revisions; additional revision time is billed at $110 per hour.',
    },
    {
        id: 11,
        question: 'What do I need to join a Sessionwire session?',
        answer:
            'All you need is a microphone and headphones (a camera is optional), a stable internet connection, and either Google Chrome or the free Sessionwire desktop app. Sessionwire lets us pass audio directly into another studio’s DAW for true studio-to-studio collaboration. If your session requires this then you will need to download the Sessionwire DAW plugins for your operating system from their website. We can help you set this up if needed before the session.',
    },
    {
        id: 12,
        question: 'How does mobile recording work?',
        answer:
            'Mobile recording means we bring the Strangeways rig to your venue or space—concert halls, churches, theatres, and more. Before the date, we do pre-production to understand the room, power, and stage layout, decide which audio and video gear to bring, and clarify what the venue provides versus what we supply. We will consult with you ahead of time to design a setup that matches your goals and budget.',
    },
    {
        id: 13,
        question: 'Where is the studio, and what is access like?',
        answer:
            'The studio is located in North Nanaimo on Vancouver Island. The exact address is provided once you submit a booking request and deposit. There is ample parking, a ground-level load-in door for gear, and the studio is wheelchair accessible.',
    },
    {
        id: 14,
        question: 'What are your session and rehearsal hours?',
        answer:
            'Recording sessions typically run 9am-5pm or 10am-6pm on weekdays, 10am-6pm on Saturdays, and 11am-7pm on Sundays. Rehearsals run 6pm-10pm Monday-Thursday. The building has a hard limit of 50 people to comply with building and fire codes.',
    },
    {
        id: 15,
        question: 'Do you have a lounge or place to eat and relax?',
        answer:
            'Yes. The studio includes a lobby and lounge, a kitchenette (hot plate, kettle, microwave, mini convection oven, coffee machine), a bathroom, and an outdoor patio—plenty of room to take breaks, eat, or work between takes.',
    },
    {
        id: 16,
        question: 'What payment methods do you accept?',
        answer:
            'We accept e-transfer (preferred), credit/debit card, cash, and cheque. Card payments are processed through Stripe and a transaction fee is added at the time of payment. This fee can be avoided by paying via e-transfer, cash, or cheque.',
    },
    {
        id: 17,
        question: 'Who owns the recordings and masters?',
        answer:
            'By default, artists own their masters and underlying compositions. If a member of our team is working with you in a producer or co-writer capacity, those terms will be defined in a separate contract agreed upon in advance.',
    },
    {
        id: 18,
        question: 'Can you help with funding or grants?',
        answer:
            'Yes. Our facility is approved by Creative BC for Music and Sound Recording grants that can cover up to 75% of a recording budget, and we have worked on projects funded by the Canada Council for the Arts. If you would like to explore funding options, book a free 30-minute consultation and we can talk through what might fit your project.',
    },
]
